import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './AddMoodSheet.css';

const FACES = [
  { valence: -2, emoji: '😞', label: 'Awful', group: 'low', defaultMood: 'sad' },
  { valence: -1, emoji: '😕', label: 'Low', group: 'low', defaultMood: 'frustrated' },
  { valence: 0, emoji: '😐', label: 'Okay', group: 'neutral', defaultMood: 'neutral' },
  { valence: 1, emoji: '🙂', label: 'Good', group: 'positive', defaultMood: 'content' },
  { valence: 2, emoji: '😄', label: 'Great', group: 'positive', defaultMood: 'joy' },
];

const RAMP = {
  '-2': '#B4533E',
  '-1': '#C48C62',
  '0': '#D4B896',
  '1': '#789E94',
  '2': '#2D7A76',
};

function AddMoodSheet({ open, onClose, moodCategories, onSubmit, posting, error, success }) {
  const [face, setFace] = useState(null);
  const [word, setWord] = useState(null);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (!open) {
      setFace(null);
      setWord(null);
      setShowMore(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const selectFace = (f) => {
    setFace(f);
    setWord(f.defaultMood);
    setShowMore(false);
  };

  const wordList = face && moodCategories ? moodCategories[face.group] || [] : [];
  const visibleWords = showMore ? wordList : wordList.slice(0, 8);
  const hasMore = wordList.length > 8;
  const canSubmit = !!face && !!word && !posting && !success;

  const sheet = (
    <div className="add-mood-root" role="presentation">
      <button type="button" className="add-mood-scrim" aria-label="Dismiss" onClick={onClose} />
      <div
        className="add-mood-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-mood-title"
      >
        <div className="add-mood-handle" aria-hidden />
        <div className="add-mood-header">
          <h2 id="add-mood-title" className="add-mood-title">
            How are you feeling?
          </h2>
          <button type="button" className="add-mood-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {success ? (
          <div className="add-mood-success" role="status" aria-live="polite">
            <span className="add-mood-success-icon" aria-hidden>
              ✓
            </span>
            Logged
          </div>
        ) : (
          <>
            <div className="add-mood-faces" role="radiogroup" aria-label="Mood face">
              {FACES.map((f) => {
                const selected = face?.valence === f.valence;
                const color = RAMP[String(f.valence)];
                return (
                  <button
                    key={f.valence}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`add-mood-face${selected ? ' is-selected' : ''}`}
                    onClick={() => selectFace(f)}
                  >
                    <span
                      className="add-mood-face-emoji"
                      aria-hidden
                      style={
                        selected
                          ? {
                              background: `${color}24`,
                              boxShadow: `0 0 0 2px ${color}`,
                            }
                          : undefined
                      }
                    >
                      {f.emoji}
                    </span>
                    <span className="add-mood-face-label">{f.label}</span>
                  </button>
                );
              })}
            </div>

            {face && (
              <div className="add-mood-words">
                <div className="add-mood-words-label">Add a word · optional</div>
                <div className="add-mood-word-chips">
                  {visibleWords.map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`add-mood-word-chip${word === m ? ' is-selected' : ''}`}
                      onClick={() => setWord(m)}
                    >
                      {m}
                    </button>
                  ))}
                  {hasMore && (
                    <button
                      type="button"
                      className="add-mood-word-more"
                      onClick={() => setShowMore((v) => !v)}
                    >
                      {showMore ? '− less' : '+ more'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {error && (
              <div className="add-mood-error" role="alert">
                {error}
              </div>
            )}

            <button
              type="button"
              className="add-mood-cta"
              disabled={!canSubmit}
              onClick={() => onSubmit(word, face?.valence)}
            >
              {posting ? 'Logging…' : 'Log mood'}
            </button>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}

export default AddMoodSheet;
